<template>
  <div class='sticky top-0 border-b border-gray-200 py-3 px-3 flex gap-3 justify-between h-16 bg-white z-10 max-w-full' v-if='vars'>
    <div class='flex gap-3 shrink-1 overflow-auto'>
      <span class="pill">
        <ClipboardDocumentIcon class='w-4 h-4 shrink-0' />
        <span class='block truncate'>{{ vars.templateName }}</span>
      </span>
      <span class="pill">
        <CircleStackIcon class='w-4 h-4 shrink-0' />
        <span class='block truncate'>{{ vars.assetName }}</span>
      </span>
      <span class="pill" v-if='vars.hasAssetManager'>
        <Square3Stack3DIcon class='w-4 h-4 shrink-0' />
        <span class='block truncate'>{{ vars.assetManagerName }}</span>
      </span>
      <span class="pill">
        <CalendarIcon class='w-4 h-4 shrink-0' />
        <span class='block truncate'>
          {{ vars.assessment.year }}
          {{ vars.assessment.halfYear !== null ? `– H${vars.assessment.halfYear}` : '' }}
        </span>
      </span>
    </div>
    <div class='flex gap-3 shrink-0 items-center'>
      <div class="inline-flex rounded-md shadow-sm">
        <button type="button" class="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50" @click='actions.toggleStatus' :disabled='!vars.isDraft || !state.assessmentInterviewId'>{{ vars.isDraft ? 'Set to complete' : 'Completed' }}</button>
        <Menu as="div" class="relative -ml-px block">
          <MenuButton class="relative inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
            <span class="sr-only">Open options</span>
            <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
          </MenuButton>
          <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
            <MenuItems class="absolute right-0 z-10 -mr-1 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div class="py-1">
                <MenuItem v-slot="{ active }" v-if='vars.isDraft'>
                  <button :class="[active ? 'bg-gray-100 text-gray-900 outline-none' : 'text-gray-700', 'block px-4 py-2 text-sm w-full text-left']" @click='actions.autosave'>Save draft</button>
                </MenuItem>
                <MenuItem v-slot="{ active }" v-if='!vars.isDraft'>
                  <button :class="[active ? 'bg-gray-100 text-gray-900 outline-none' : 'text-gray-700', 'block px-4 py-2 text-sm w-full text-left']" @click='actions.toggleStatus'>Revert to draft</button>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <Component :is='vars.isDraft ? "RouterLink" : "span"' :to='assessmentCollection.getEditUrl(vars.assessment)' :class="[active && vars.isDraft ? 'bg-gray-100 text-gray-900 outline-none' : 'text-gray-700', 'block px-4 py-2 text-sm w-full text-left', !vars.isDraft ? 'opacity-50' : '']">Edit settings</Component>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
  </div>
</template>

<script setup>

import { computed } from 'vue';

import {
  Square3Stack3DIcon,
  CircleStackIcon,
  ClipboardDocumentIcon,
  CalendarIcon
} from '@heroicons/vue/24/outline';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'

import { getCollection } from '@/kernel/collections';

const assessmentCollection = getCollection('assessment');
const assessmentTemplateCollection = getCollection('assessment-template');
const organisationAssetCollection = getCollection('organisation-asset');
const organisationAssetManagerCollection = getCollection('organisation-asset-manager');

const props = defineProps({
  state: Object,
  actions: Object
});

const actionItems = [
  { name: 'Complete', href: '#' },
  { name: 'Set to draft', href: '#' },
  { name: 'Export PDF', href: '#' },
]

const statusOptions = [
  {value: 'draft', label: 'Draft'},
  {value: 'complete', label: 'Complete'}
];

const vars = computed(() => {
  const assessment = props.state.assessment;

  if (!assessment) {
    return null;
  }

  const hasAssetManager = assessment.organisationAssetManager !== null;

  return {
    assessment,
    hasAssetManager,
    templateName: assessmentTemplateCollection.getName(assessment.assessmentTemplate),
    assetName: organisationAssetCollection.getName(assessment.organisationAsset),
    assetManagerName: hasAssetManager? organisationAssetManagerCollection.getName(assessment.organisationAssetManager) : null,
    isDraft: props.state.status === 'draft'
  }
});

</script>

<style scoped>

.pill {
  @apply inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 gap-2;
}

</style>
